import type { Profile, ProfileImageKind } from "api";

/**
 * What the back office accepts, mirrored so an obviously wrong file never
 * costs an upload: `config/profile.php` in qubictron-backoffice.
 */
const ALLOWED_TYPES = ["image/jpeg", "image/png", "image/webp"];

const MAX_KB = 5120;

/** For the file input, so the picker offers the right files to begin with. */
export const IMAGE_ACCEPT = ALLOWED_TYPES.join(",");

type ImageSpec = {
  /** The label the card and the modal use for this image. */
  noun: string;
  /** Minimum size the API enforces, also the hint under the buttons. */
  hint: string;
};

export const IMAGE_SPECS: Record<ProfileImageKind, ImageSpec> = {
  avatar: { noun: "photo", hint: "Square image, at least 400×400." },
  cover: { noun: "cover", hint: "Wide image, 1128×191 or larger." },
};

/**
 * The two rules worth checking in the browser. Dimensions are left to the API:
 * reading them means decoding the file, and the message comes back the same
 * way either way.
 */
export const imageFileError = (file: File): string | null => {
  if (!ALLOWED_TYPES.includes(file.type)) {
    return "Choose a JPEG, PNG or WebP image.";
  }

  if (file.size > MAX_KB * 1024) {
    return `The image must be smaller than ${MAX_KB / 1024} MB.`;
  }

  return null;
};

/**
 * The name on the card. It falls back to the email's local part rather than to
 * nothing: an account registered through a provider may carry no name at all.
 */
export const displayName = (profile: Profile): string => {
  const name = [profile.first_name, profile.last_name]
    .map((part) => part?.trim() ?? "")
    .filter(Boolean)
    .join(" ");

  return name || profile.email.split("@")[0] || "Your profile";
};

/**
 * The design's seven positions. It is a free string on the API — the list
 * differs per role and is too early to freeze — so a value saved outside the
 * list stays selectable instead of being silently swapped for one of these.
 */
export const POSITIONS = ["Owner", "Director", "Manager", "Operations", "Sales", "Employee", "Other"];

export const positionOptions = (current: string): { value: string; label: string }[] => {
  const known = current === "" || POSITIONS.includes(current);
  const all = known ? POSITIONS : [current, ...POSITIONS];

  return all.map((value) => ({ value, label: value }));
};
